import { useState, useEffect, useMemo, useRef } from 'react';
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import FileCopyOutlinedIcon from '@mui/icons-material/FileCopyOutlined';
import ReplayOutlinedIcon from '@mui/icons-material/ReplayOutlined';
import { useAppDispatch } from 'redux/store-hooks';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import {
  learnMoreCreatorSelectors,
  upsertLearnMoreRecord,
  setCurrentContent,
} from 'redux/slices/learnMoreCreatorSlice';
import {
  SectionContentDto,
  DEFAULT_ACCORDION_ENTRY,
  DEFAULT_COPY_ENTRY,
  LearnMoreContentSchema,
  LearnMoreContentData,
} from '@act/shared/data-transfer-objects';
import DraggableSection from './DraggableSectionCard';
import { useApiCall } from 'redux/utils/api-hook';
import { surveyAdminRequests } from 'redux/requests/survey-admin.requests';
import { FormBuilder } from '@act/storybook';
import { useToast } from '@act/react-components/app/ReusableToast';
import { createLearnMoreFormConfig } from './learn-more-form-config';
import useConfirmationAlertDialog from '@act/react-components/dialog-forms/hooks/use-confirmation-alert-dialog';

interface DeleteDialogState {
  open: boolean;
  type: 'section' | 'entry';
  sectionIndex: number;
  entryIndex?: number;
}

const LearnMoreCreator = () => {
  const dispatch = useAppDispatch();
  const openConfirmationDialog = useConfirmationAlertDialog();
  const toast = useToast();
  const [learnMoreRecord, isSavingRecord, saveLearnMoreRecord] = useApiCall(
    surveyAdminRequests.createLearnMoreCreatorRecord
  );
  const [updatedLearnMoreRecord, isUpdatingRecord, updateLearnMoreRecord] =
    useApiCall(surveyAdminRequests.updateLearnMoreCreatorRecord);

  const allLearnMoreRecords =
    learnMoreCreatorSelectors.useSelectLearnMoreRecords();
  const currentContentFromRedux =
    learnMoreCreatorSelectors.useSelectLearnMoreCurrentContent();

  const [deleteDialog, setDeleteDialog] = useState<DeleteDialogState>({
    open: false,
    type: 'section',
    sectionIndex: -1,
  });

  const [currentRecordId, setCurrentRecordId] = useState<number>(
    currentContentFromRedux?.id ?? -1
  );
  const [lastLoadedRecordId, setLastLoadedRecordId] = useState<number>(
    currentContentFromRedux?.id ?? -1
  );
  const isInitialMount = useRef(true);

  const { control, reset, watch, handleSubmit, formState, setValue } =
    useForm<LearnMoreContentData>({
      mode: 'onChange',
      resolver: zodResolver(LearnMoreContentSchema),
      defaultValues: {
        title: currentContentFromRedux?.title ?? '',
        sections: currentContentFromRedux?.sections ?? [],
        selectedLearnMoreRecordId: currentContentFromRedux?.id ?? -1,
      },
    });

  const currentSelectRecordId = watch('selectedLearnMoreRecordId');
  const watchedTitle = watch('title');
  const watchedSections = watch('sections');

  // Sync form changes to Redux (skip on initial mount to avoid overwriting)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    const currentContent = {
      title: watchedTitle,
      sections: watchedSections as SectionContentDto[],
      id: currentRecordId !== -1 ? currentRecordId : undefined,
    };
    dispatch(setCurrentContent(currentContent));
  }, [watchedTitle, watchedSections, currentRecordId, dispatch]);

  useEffect(() => {
    // Only reset if we're loading a completely different record
    if (currentSelectRecordId !== lastLoadedRecordId) {
      if (currentSelectRecordId === -1) {
        reset({
          title: '',
          sections: [],
          selectedLearnMoreRecordId: -1,
        });
        setCurrentRecordId(-1);
        dispatch(
          setCurrentContent({
            title: '',
            sections: [],
          })
        );
        setLastLoadedRecordId(-1);
      } else {
        const selectedRecord = allLearnMoreRecords.find(
          record => record.id === currentSelectRecordId
        );
        if (selectedRecord) {
          reset({
            title: selectedRecord.learnMoreContent.title,
            sections: selectedRecord.learnMoreContent.sections,
            selectedLearnMoreRecordId: currentSelectRecordId,
          });
          setCurrentRecordId(currentSelectRecordId);
          dispatch(
            setCurrentContent({
              ...selectedRecord.learnMoreContent,
              id: currentSelectRecordId,
            })
          );
          setLastLoadedRecordId(currentSelectRecordId);
        }
      }
    }
  }, [
    currentSelectRecordId,
    lastLoadedRecordId,
    allLearnMoreRecords,
    reset,
    dispatch,
  ]);

  useEffect(() => {
    if (updatedLearnMoreRecord) {
      dispatch(upsertLearnMoreRecord(updatedLearnMoreRecord));
      setCurrentRecordId(updatedLearnMoreRecord.id);
      setValue('selectedLearnMoreRecordId', updatedLearnMoreRecord.id);
      toast('Learn More Record updated successfully');
    }
  }, [updatedLearnMoreRecord]);

  useEffect(() => {
    if (learnMoreRecord) {
      dispatch(upsertLearnMoreRecord(learnMoreRecord));
      setCurrentRecordId(learnMoreRecord.id);
      setValue('selectedLearnMoreRecordId', learnMoreRecord.id);
      toast('Learn More Record created successfully');
    }
  }, [learnMoreRecord]);

  const handleAddSection = (type: 'accordion' | 'copy') => {
    const newSection: SectionContentDto =
      type === 'accordion'
        ? {
            type: 'accordion',
            sortOrder: watchedSections.length + 1,
            entries: [{ ...DEFAULT_ACCORDION_ENTRY }],
          }
        : {
            type: 'copy',
            sortOrder: watchedSections.length + 1,
            entries: [{ ...DEFAULT_COPY_ENTRY }],
            isHighlighted: false,
          };

    const newSections = [...watchedSections, newSection];
    setValue('sections', newSections, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleMoveSection = (dragIndex: number, hoverIndex: number) => {
    const dragSection = watchedSections[dragIndex];
    const newSections = [...watchedSections];
    newSections.splice(dragIndex, 1);
    newSections.splice(hoverIndex, 0, dragSection);

    const reorderedSections = newSections.map((section, index) => ({
      ...section,
      sortOrder: index + 1,
    }));

    setValue('sections', reorderedSections, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const handleSectionChange = (
    index: number,
    updatedSection: SectionContentDto
  ) => {
    const newSections = [...watchedSections];
    newSections[index] = updatedSection;
    setValue('sections', newSections, {
      shouldDirty: true,
      shouldValidate: true,
    });
  };

  const formConfig = useMemo(
    () => createLearnMoreFormConfig(allLearnMoreRecords),
    [allLearnMoreRecords]
  );

  const handleReset = async () => {
    const shouldResetLearnMore = await openConfirmationDialog({
      title: 'Reset survey confirmation',
      icon: <ErrorOutlineIcon />,
      iconColor: 'error',
      iconBorderColor: 'errorContainer',
      message:
        'Are you sure you want to reset this learn more content? You will lose all unsaved changes, and be starting from scratch.',
      confirmationText: 'Reset Learn More Content',
      confirmButtonColor: 'error',
    });
    if (shouldResetLearnMore) {
      reset({ selectedLearnMoreRecordId: -1, title: '', sections: [] });
      setCurrentRecordId(-1);
      setLastLoadedRecordId(-1);
      dispatch(
        setCurrentContent({
          title: '',
          sections: [],
        })
      );
      toast('Learn More Content reset successfully');
    }
  };

  const handleDuplicate = () => {
    if (currentRecordId !== -1) {
      saveLearnMoreRecord({
        params: null,
        body: {
          learnMoreContent: {
            title: `${watchedTitle} (Copy)`,
            sections: watchedSections as SectionContentDto[],
          },
        },
      });
    }
  };

  const handleDeleteClick = (sectionIndex: number, entryIndex?: number) => {
    setDeleteDialog({
      open: true,
      type: entryIndex !== undefined ? 'entry' : 'section',
      sectionIndex,
      entryIndex,
    });
  };

  const handleDeleteConfirm = () => {
    const { type, sectionIndex, entryIndex } = deleteDialog;

    if (type === 'section') {
      const newSections = watchedSections
        .filter((_, index) => index !== sectionIndex)
        .map((section, index) => ({
          ...section,
          sortOrder: index + 1,
        }));
      setValue('sections', newSections, {
        shouldDirty: true,
        shouldValidate: true,
      });
    } else if (type === 'entry' && entryIndex !== undefined) {
      const section = watchedSections[sectionIndex];
      if (section.entries.length > 1) {
        const newEntries = section.entries.filter((_, i) => i !== entryIndex);
        const updatedSection = {
          ...section,
          entries: newEntries.map((entry, i) => ({
            ...entry,
            sortOrder: i + 1,
          })),
        };
        const newSections = [...watchedSections];
        newSections[sectionIndex] = updatedSection as SectionContentDto;
        setValue('sections', newSections, {
          shouldDirty: true,
          shouldValidate: true,
        });
      }
    }

    setDeleteDialog({ open: false, type: 'section', sectionIndex: -1 });
  };

  const handleDeleteCancel = () => {
    setDeleteDialog({ open: false, type: 'section', sectionIndex: -1 });
  };

  const onSubmit = (data: LearnMoreContentData) => {
    if (currentRecordId !== -1) {
      updateLearnMoreRecord({
        params: { id: currentRecordId },
        body: {
          learnMoreContent: {
            title: data.title,
            sections: data.sections as SectionContentDto[],
          },
        },
      });
    } else {
      saveLearnMoreRecord({
        params: null,
        body: {
          learnMoreContent: {
            title: data.title,
            sections: data.sections as SectionContentDto[],
          },
        },
      });
    }
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      <Box
        component="div"
        sx={{
          padding: '8px 16px',
          backgroundColor: 'background.paper',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="h6" sx={{ color: 'text.primary' }}>
          Learn More Content
        </Typography>
        <Box sx={{ display: 'flex', gap: '8px' }}>
          <Tooltip title="Clear content">
            <IconButton onClick={handleReset}>
              <ReplayOutlinedIcon />
            </IconButton>
          </Tooltip>
          {currentRecordId !== -1 && (
            <Tooltip title="Duplicate content">
              <IconButton onClick={handleDuplicate}>
                <FileCopyOutlinedIcon />
              </IconButton>
            </Tooltip>
          )}
          <Button
            variant="outlined"
            disabled={!formState.isValid || isSavingRecord || isUpdatingRecord}
            onClick={handleSubmit(onSubmit)}
          >
            {isSavingRecord || isUpdatingRecord ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                Saving... <CircularProgress size={16} />
              </Box>
            ) : (
              'Save content'
            )}
          </Button>
        </Box>
      </Box>
      <FormBuilder
        config={formConfig}
        control={control}
        formState={formState}
      />
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          padding: '16px 8px 0',
        }}
      >
        <Typography variant="h6" sx={{ mb: 2, color: 'text.primary' }}>
          Sections
        </Typography>

        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '8px',
            height: '100%',
            overflowY: 'auto',
            padding: '8px 8px 0',
          }}
        >
          {watchedSections.map((section, index) => (
            <DraggableSection
              key={index}
              section={section as SectionContentDto}
              index={index}
              onMove={handleMoveSection}
              onChange={handleSectionChange}
              onDeleteClick={handleDeleteClick}
              onDeleteEntry={handleDeleteClick}
            />
          ))}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', gap: '16px', margin: '16px auto' }}>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleAddSection('accordion')}
        >
          Add Accordion Section
        </Button>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleAddSection('copy')}
        >
          Add Copy Section
        </Button>
      </Box>

      <Dialog
        open={deleteDialog.open}
        onClose={handleDeleteCancel}
        aria-labelledby="delete-dialog-title"
      >
        <DialogTitle id="delete-dialog-title">
          Delete {deleteDialog.type === 'section' ? 'Section' : 'Entry'}
        </DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this {deleteDialog.type}? This
            action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default LearnMoreCreator;
